import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, runTransaction, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import firebaseAuthService from '../services/firebaseAuthService';
import accessControlService from '../services/accessControlService';
import deletedItemsService from '../services/deletedItemsService';
import { ConfirmPanel, Field, Icon, ICON, Panel, formatDate, formatHa, nameOf } from './ui';
import {
  DEFAULT_RATES, DEFAULT_TYPES, allTypesOf, applyRecord, budgetFor, buildPeople, buildRecord, canFinish, correctRates,
  distributionOf, fromQuantity, fromSurface, fromValue, hasPrice, isExpired, isExpiringSoon, kgPerHa, matchesSearch, maxQuantity,
  perHaLabel, productName, productUnit, recordTotal, sortPeople, startSession, stepFor, sumSurfaces, sumValues, termsFor, valuePerHa,
  yearStatistics,
} from './arendaLogic';
import { chitantaHtml, contractHtml, tableHtml } from './arendaDocuments';
import './ArendaManager.css';

const SECTION_KEY = 'arenda-open-company';
const lei = (value) => `${Math.round(value || 0).toLocaleString('ro-RO')} lei`;
const decimalText = (value) => value.replace(',', '.').replace(/[^0-9.]/g, '');
const wholeText = (value) => value.replace(/[^0-9]/g, '');

const readCached = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return Array.isArray(saved?.data) ? saved.data : null;
  } catch { return null; }
};

const yearsToShow = (records) => {
  const now = new Date().getFullYear();
  const first = Math.min(2025, now, ...records.map((record) => Number(record.year) || now));
  return Array.from({ length: now + 3 - first + 1 }, (_, index) => first + index);
};

const givenAt = (record) => {
  const when = record.takenTimestamp || record.timestamp || record.date;
  return when ? new Date(when).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' }) : '—';
};
const newestFirst = (a, b) => new Date(b.takenTimestamp || b.timestamp || 0) - new Date(a.takenTimestamp || a.timestamp || 0);

const productsLine = (record, types) => {
  const { amounts } = distributionOf(record);
  return Object.entries(amounts)
    .filter(([, amount]) => Number(amount) > 0)
    .map(([id, amount]) => `${productName(id, types)} ${Number(amount).toLocaleString('ro-RO')} ${productUnit(id, types)}`)
    .join(' · ');
};

// ---------- Distribution ----------

const ProductCard = ({ type, session, rates, onChange, onDelay }) => {
  const { id } = type;
  const priced = hasPrice(id, rates);
  const amount = Number(session.amounts[id]) || 0;
  const max = priced ? maxQuantity(id, session, rates) : 0;
  const step = priced ? stepFor(id, rates) : 1;
  const over = amount > max;

  if (!priced) {
    return (
      <section className="ar-product is-off">
        <div className="ar-product-head"><strong>{type.name}</strong></div>
        <p className="pm-muted">Nu are preț. Îl poți pune din Tarife.</p>
      </section>
    );
  }

  const setAmount = (next) => onChange(id, fromQuantity(id, String(Math.max(0, next)), rates));

  return (
    <section className={`ar-product ${amount > 0 ? 'is-on' : ''} ${over ? 'is-over' : ''}`}>
      <div className="ar-product-head">
        <strong>{type.name}</strong>
        <span>{perHaLabel(id, rates).toLocaleString('ro-RO')} {type.unitType}/ha{id !== 'money' ? ` · ${rates[id]} lei/${type.unitType}` : ''}</span>
      </div>

      <div className="ar-stepper">
        <button type="button" aria-label={`Mai puțin ${type.name}`} disabled={amount <= 0} onClick={() => setAmount(amount - step)}><Icon d={ICON.minus} /></button>
        <label>
          <input inputMode="numeric" aria-label={`Cantitate ${type.name}`} placeholder="0" value={session.amounts[id] || ''}
            onChange={(e) => onChange(id, fromQuantity(id, wholeText(e.target.value), rates))} />
          <span>{type.unitType}</span>
        </label>
        <button type="button" aria-label={`Mai mult ${type.name}`} disabled={amount >= max} onClick={() => setAmount(Math.min(max, amount + step))}><Icon d={ICON.plus} /></button>
      </div>

      <div className="pm-two">
        <Field label="Hectare">
          <input inputMode="decimal" aria-label={`Hectare ${type.name}`} placeholder="0.00" value={session.surfaces[id] || ''}
            onChange={(e) => onChange(id, fromSurface(id, decimalText(e.target.value), rates))} />
        </Field>
        {id !== 'money' && (
          <Field label="Valoare (lei)">
            <input inputMode="numeric" aria-label={`Valoare ${type.name}`} placeholder="0" value={session.values[id] || ''}
              onChange={(e) => onChange(id, fromValue(id, wholeText(e.target.value), rates))} />
          </Field>
        )}
      </div>

      <div className="ar-product-foot">
        {over
          ? <span className="ar-over">Prea mult. Maxim {max.toLocaleString('ro-RO')} {type.unitType}.</span>
          : <span>Maxim {max.toLocaleString('ro-RO')} {type.unitType}</span>}
        <button type="button" className="ar-link" disabled={max === amount} onClick={() => setAmount(max)}>Pune tot restul</button>
      </div>

      {id !== 'money' && amount > 0 && (
        <label className="ar-switch">
          <input type="checkbox" checked={Boolean(session.delayed[id])} onChange={(e) => onDelay(id, e.target.checked)} />
          <span>Ridică în toamnă</span>
        </label>
      )}
    </section>
  );
};

const DistributionPanel = ({ session, setSession, rates, types, saving, onSave, onClose }) => {
  const { person } = session;
  const budget = budgetFor(person.totalSuprafata, rates);
  const used = sumValues(session.values);
  const over = used > budget;
  const leftHa = person.totalSuprafata - sumSurfaces(session.surfaces);

  const change = (patch) => setSession((prev) => ({ ...prev, ...patch(prev), changed: true }));
  const onChange = (id, { surface, amount, value }) => change((prev) => ({
    surfaces: { ...prev.surfaces, [id]: surface },
    amounts: { ...prev.amounts, [id]: amount },
    values: { ...prev.values, [id]: value },
  }));
  const onDelay = (id, on) => change((prev) => ({ delayed: { ...prev.delayed, [id]: on } }));

  const removedTypes = Object.keys(session.amounts).filter((id) => Number(session.amounts[id]) > 0 && !types.some((type) => type.id === id));
  const otherCnp = session.other.cnp || '';

  return (
    <Panel title={session.record ? 'Modifică distribuția' : 'Distribuie arenda'} subtitle={person.nume} onClose={onClose}
      className="ar-session"
      footer={(
        <>
          <button type="button" className="pm-btn-soft pm-only-wide" onClick={onClose}>Anulează</button>
          <button type="button" className="pm-btn-accent" disabled={saving || !canFinish(session, rates)} onClick={onSave}>
            {saving ? 'Se salvează…' : `${session.record ? 'Salvează' : 'Finalizează'} și printează`}
          </button>
        </>
      )}>
      <div className={`ar-budget ${over ? 'is-over' : ''}`}>
        <p className="ar-budget-big"><strong>{lei(used)}</strong> din {lei(budget)}</p>
        <div className="ar-bar"><i style={{ width: `${budget ? Math.min(100, (used / budget) * 100) : 0}%` }} /></div>
        <p className="ar-budget-left">
          {over ? `Depășește cu ${lei(used - budget)}` : `Rămân ${lei(budget - used)} · ${formatHa(Math.max(0, leftHa))} ha`}
        </p>
      </div>

      {person.otherFirms?.length > 0 && (
        <p className="pm-note">Are parcele și la {person.otherFirms.join(', ')}. Arenda se calculează cu tarifele firmei {person.firma}, unde are cel mai mult teren.</p>
      )}
      <div className="pm-note">
        {person.firma && <strong className="ar-tline">{person.firma}</strong>}
        {formatHa(person.totalSuprafata)} ha × {lei(valuePerHa(rates))}/ha ({kgPerHa(rates)} kg grâu × {rates.grau} lei/kg)
        {person.tGroups.length > 0 && <TGroups groups={person.tGroups} />}
      </div>

      {types.map((type) => (
        <ProductCard key={type.id} type={type} session={session} rates={rates} onChange={onChange} onDelay={onDelay} />
      ))}

      {removedTypes.map((id) => (
        <p key={id} className="pm-note">
          {productName(id, types)}: {session.amounts[id]} {productUnit(id, types)}, {lei(session.values[id])}. Produsul nu mai este în listă.
        </p>
      ))}

      <section className="ar-product">
        <label className="ar-switch">
          <input type="checkbox" checked={session.pickedUp} onChange={(e) => change(() => ({ pickedUp: e.target.checked }))} />
          <span>Ridică altă persoană</span>
        </label>
        {session.pickedUp && (
          <>
            <Field label="Nume" error={!session.other.nume?.trim()}>
              <input value={session.other.nume} onChange={(e) => change((prev) => ({ other: { ...prev.other, nume: e.target.value } }))} />
            </Field>
            <Field label="CNP" error={otherCnp.length !== 13} hint={otherCnp.length !== 13 ? `Are ${otherCnp.length} din 13 cifre` : undefined}>
              <input inputMode="numeric" maxLength={13} value={otherCnp}
                onChange={(e) => change((prev) => ({ other: { ...prev.other, cnp: wholeText(e.target.value) } }))} />
            </Field>
          </>
        )}
      </section>
    </Panel>
  );
};

const TGroups = ({ groups }) => (
  <>
    {groups.map((group) => (
      <span key={group.comuna} className="ar-tline">
        <b>{group.comuna}:</b> {group.numbers.map((t) => `T${t}`).join(', ')}
      </span>
    ))}
  </>
);

// ---------- Person ----------

const PersonPanel = ({ person, record, history, rates, types, year, onClose, onDistribute, onReceipt, onPrintRecord, onContract, onDelete }) => {
  const expired = isExpired(person);
  const saved = record ? distributionOf(record) : null;
  const canDistribute = !person.orphan && !expired;
  const showContract = !person.orphan && isExpiringSoon(person);

  return (
    <Panel title={person.nume} subtitle={`CNP ${person.cnp}`} onClose={onClose}>
      {expired && <p className="pm-error">Contractul a expirat. Reînnoiește-l înainte să distribui arenda.</p>}
      {person.orphan && <p className="pm-note">Persoana nu mai are parcele. Distribuția din {year} rămâne în evidență.</p>}

      <div className="pm-actions">
        {canDistribute && (
          <button type="button" className="is-primary" onClick={onDistribute}>
            <Icon d={record ? ICON.edit : ICON.play} />{record ? 'Modifică' : 'Distribuie'}
          </button>
        )}
        {record && <button type="button" onClick={onReceipt}><Icon d={ICON.print} />Chitanța</button>}
        {showContract && <button type="button" onClick={onContract}><Icon d={ICON.doc} />Contract nou</button>}
        {record && <button type="button" className="is-danger" onClick={onDelete}><Icon d={ICON.trash} />Șterge distribuția</button>}
      </div>

      {record && (
        <div className="pm-card">
          <h3>Primit în {year}</h3>
          <dl className="pm-rows">
            {Object.keys(saved.amounts).filter((id) => Number(saved.amounts[id]) > 0).map((id) => (
              <div key={id}>
                <dt>{productName(id, types)}{saved.delayedPickup[id] ? ' (în toamnă)' : ''}</dt>
                <dd>{Number(saved.amounts[id]).toLocaleString('ro-RO')} {productUnit(id, types)} · {lei(saved.values[id])}</dd>
              </div>
            ))}
            <div><dt>Total</dt><dd>{lei(recordTotal(record))}</dd></div>
            <div><dt>Ridicat</dt><dd>{givenAt(record)}</dd></div>
            {saved.pickedUpBy && <div><dt>Ridicat de</dt><dd>{saved.pickedUpBy.nume} ({saved.pickedUpBy.cnp})</dd></div>}
          </dl>
        </div>
      )}

      {history.length > 0 && (
        <div className="pm-card">
          <h3>Istoric arendă</h3>
          <div className="ar-history">
            {history.map((item) => (
              <div key={item.id || `${item.cnp}_${item.year}`}>
                <p>
                  <strong>{item.year} · {lei(recordTotal(item))}</strong>
                  <span>Dată la {givenAt(item)}</span>
                  {productsLine(item, types) && <span>{productsLine(item, types)}</span>}
                  {distributionOf(item).pickedUpBy && <span>Ridicat de {distributionOf(item).pickedUpBy.nume}</span>}
                  {item.lastEdited && <span>Modificată la {new Date(item.lastEdited).toLocaleDateString('ro-RO')}</span>}
                </p>
                <button type="button" className="ar-link" aria-label={`Chitanța din ${item.year}`} onClick={() => onPrintRecord(item)}>Chitanța</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!person.orphan && (
        <div className="pm-card">
          <h3>Teren și contract</h3>
          <dl className="pm-rows">
            <div><dt>Suprafață</dt><dd>{formatHa(person.totalSuprafata)} ha</dd></div>
            <div><dt>Buget {year}</dt><dd>{lei(budgetFor(person.totalSuprafata, rates))}</dd></div>
            <div><dt>Parcele</dt><dd>{person.parcels.length}</dd></div>
            {person.tGroups.map((group) => (
              <div key={group.comuna}><dt>{group.comuna}</dt><dd>{group.numbers.map((t) => `T${t}`).join(', ')}</dd></div>
            ))}
            <div><dt>Domiciliu</dt><dd>{person.domiciliat}</dd></div>
            {person.firma && <div><dt>Firmă</dt><dd>{person.firma}</dd></div>}
            <div><dt>Contract din</dt><dd>{formatDate(person.dataContract)}</dd></div>
            <div>
              <dt>Expiră</dt>
              <dd className={isExpiringSoon(person) ? 'pm-warn' : ''}>{person.contractExpiration?.toLocaleDateString('ro-RO') || '—'}</dd>
            </div>
          </dl>
        </div>
      )}
    </Panel>
  );
};

// ---------- Prices and products ----------

const RatesPanel = ({ company, own, rates, types, onSave, onAddType, onRemoveType, onClose }) => {
  const priced = types.filter((type) => type.id !== 'grau' && type.id !== 'money');
  const [draft, setDraft] = useState(() => Object.fromEntries(['grau', ...priced.map((type) => type.id)].map((id) => [id, String(rates[id] ?? '')])));
  const [baseKg, setBaseKg] = useState(String(kgPerHa(rates)));
  const [newType, setNewType] = useState({ name: '', unit: '', price: '1' });
  const [tried, setTried] = useState(false);
  const grauPrice = parseFloat(draft.grau) || 0;
  const kg = parseFloat(baseKg) || 0;

  const perHa = (id) => {
    const price = parseFloat(draft[id]) || 0;
    return price > 0 ? Math.round((kg * grauPrice) / price) : 0;
  };

  const save = () => {
    setTried(true);
    if (grauPrice <= 0 || kg <= 0) return;
    const next = { ...rates, baseKg: kg };
    Object.entries(draft).forEach(([id, value]) => { next[id] = parseFloat(value) || 0; });
    onSave(next);
  };

  const add = async () => {
    const name = newType.name.trim();
    const unit = newType.unit.trim();
    if (!name || !unit) return;
    const added = await onAddType(name, unit, parseFloat(newType.price) || 1);
    if (added) {
      setDraft((prev) => ({ ...prev, [added]: String(parseFloat(newType.price) || 1) }));
      setNewType({ name: '', unit: '', price: '1' });
    }
  };

  return (
    <Panel title={company ? `Tarife ${company}` : 'Tarife'} subtitle="Se aplică doar acestei firme, la distribuțiile noi" onClose={onClose}
      footer={(
        <>
          <button type="button" className="pm-btn-soft pm-only-wide" onClick={onClose}>Anulează</button>
          <button type="button" className="pm-btn-primary" onClick={save}>Salvează tarifele</button>
        </>
      )}>
      {company && !own && <p className="pm-note">Firma folosește încă tarifele generale ale fermei. După ce salvezi, va avea tarifele ei.</p>}
      <div className="pm-two">
        <Field label="Kg grâu pe hectar" error={tried && kg <= 0}>
          <input inputMode="decimal" value={baseKg} onChange={(e) => setBaseKg(decimalText(e.target.value))} />
        </Field>
        <Field label="Preț grâu (lei/kg)" error={tried && grauPrice <= 0}>
          <input inputMode="decimal" value={draft.grau} onChange={(e) => setDraft({ ...draft, grau: decimalText(e.target.value) })} />
        </Field>
      </div>
      <p className="mo-tip">Un hectar: {kg.toLocaleString('ro-RO')} kg × {grauPrice} lei = {lei(kg * grauPrice)}</p>

      {priced.map((type) => (
        <Field key={type.id} label={`Preț ${type.name} (lei/${type.unitType})`} hint={`Un hectar: ${perHa(type.id).toLocaleString('ro-RO')} ${type.unitType}`}>
          <input inputMode="decimal" value={draft[type.id] ?? ''} onChange={(e) => setDraft({ ...draft, [type.id]: decimalText(e.target.value) })} />
        </Field>
      ))}

      <div className="pm-card">
        <h3>Produsele acestei firme</h3>
        <div className="ar-types">
          {types.map((type) => (
            <div key={type.id}>
              <span><strong>{type.name}</strong> · {type.unit}</span>
              {!['money', 'grau'].includes(type.id) && (
                <button type="button" className="pm-link-danger" onClick={() => onRemoveType(type)}>Șterge</button>
              )}
            </div>
          ))}
        </div>
        <h3>Adaugă un produs</h3>
        <Field label="Nume"><input placeholder="ex: Ovăz" value={newType.name} onChange={(e) => setNewType({ ...newType, name: e.target.value })} /></Field>
        <div className="pm-two">
          <Field label="Unitate"><input placeholder="ex: kg sau l" value={newType.unit} onChange={(e) => setNewType({ ...newType, unit: e.target.value })} /></Field>
          <Field label="Preț (lei)"><input inputMode="decimal" value={newType.price} onChange={(e) => setNewType({ ...newType, price: decimalText(e.target.value) })} /></Field>
        </div>
        <button type="button" className="pm-btn-soft" disabled={!newType.name.trim() || !newType.unit.trim()} onClick={add}>Adaugă produsul</button>
      </div>
    </Panel>
  );
};

const StatsPanel = ({ stats, year, onClose }) => (
  <Panel title={`Statistici ${year}`} onClose={onClose}>
    <div className="ar-tiles">
      <div><strong>{stats.total}</strong><span>Arendași</span></div>
      <div><strong>{stats.received}</strong><span>Au primit ({stats.percent}%)</span></div>
      <div><strong>{stats.waiting}</strong><span>Nu au primit</span></div>
    </div>
    <div className="pm-card">
      <h3>Valoare</h3>
      <dl className="pm-rows">
        <div><dt>Distribuit</dt><dd>{lei(stats.distributed)}</dd></div>
        <div><dt>Total de distribuit</dt><dd>{lei(stats.possible)}</dd></div>
        <div><dt>Rămâne</dt><dd>{lei(stats.remaining)}</dd></div>
      </dl>
    </div>
    <div className="pm-card">
      <h3>Produse date</h3>
      {stats.products.length === 0 && <p className="pm-muted">Nimic încă în {year}.</p>}
      <dl className="pm-rows">
        {stats.products.map((product) => (
          <div key={product.id}><dt>{product.name}</dt><dd>{product.amount.toLocaleString('ro-RO')} {product.unit}</dd></div>
        ))}
      </dl>
    </div>
  </Panel>
);

const HistoryPanel = ({ records, company, types, year, onOpen, onClose }) => (
  <Panel title={`Istoric ${year}`} subtitle={`${company || 'Fără firmă'}: când a fost dată arenda, cele mai noi primele`} onClose={onClose}>
    {records.length === 0 && <p className="pm-empty">Nimeni nu a primit încă în {year}.</p>}
    <div className="pm-list ar-history-list">
      {records.map((record) => (
        <button key={record.id || record.cnp} type="button" className="pm-row" onClick={() => onOpen(record.cnp)}>
          <div className="pm-row-main">
            <strong>{record.nume}</strong>
            <span>Dată la {givenAt(record)}</span>
            {productsLine(record, types) && <span>{productsLine(record, types)}</span>}
            {distributionOf(record).pickedUpBy && <span>Ridicat de {distributionOf(record).pickedUpBy.nume}</span>}
          </div>
          <span className="pm-row-end">{lei(recordTotal(record))}</span>
        </button>
      ))}
    </div>
  </Panel>
);

const TablePanel = ({ year, company, total, onOpen, onClose }) => (
  <Panel title={`Tabel arendă ${year}`} subtitle={company || 'Fără firmă'} onClose={onClose}
    footer={<button type="button" className="pm-btn-primary" onClick={onOpen}>Deschide tabelul</button>}>
    <p className="pm-note">Toate distribuțiile acestei firme în {year}, în ordine alfabetică: {total}.</p>
  </Panel>
);

// Shows the document inside the app, because home-screen apps block new windows.
const DocumentPanel = ({ title, html, onClose, onError }) => {
  const frame = useRef(null);
  const page = useMemo(() => html.replace('</head>', '<style>.no-print,.controls{display:none!important}</style></head>'), [html]);
  const print = () => {
    try { frame.current.contentWindow.print(); } catch { onError('Printarea nu a pornit. Încearcă din browser.'); }
  };
  return (
    <Panel title={title} onClose={onClose} className="ar-doc"
      footer={(
        <>
          <button type="button" className="pm-btn-soft" onClick={onClose}>Închide</button>
          <button type="button" className="pm-btn-primary" onClick={print}><Icon d={ICON.print} size={18} />Printează</button>
        </>
      )}>
      <iframe ref={frame} title={title} srcDoc={page} sandbox="allow-same-origin allow-modals" className="ar-doc-frame" />
    </Panel>
  );
};

// ---------- Page ----------

const ArendaManager = ({ testStore }) => {
  const farmId = testStore ? null : localStorage.getItem('currentFarmId');

  const [parcels, setParcels] = useState(() => testStore?.parcels || readCached(`parcelsData_${farmId}`) || []);
  const [records, setRecords] = useState(() => testStore?.arenda || readCached(`arendaData_${farmId}`) || []);
  const [rates, setRates] = useState(() => {
    if (testStore) return correctRates(testStore.rates);
    try {
      const cached = JSON.parse(localStorage.getItem(`farms_${farmId}_settings_grauRates`));
      return cached ? correctRates(cached) : DEFAULT_RATES;
    } catch { return DEFAULT_RATES; }
  });
  const [types, setTypes] = useState(testStore?.types || DEFAULT_TYPES);
  const [companies, setCompanies] = useState(testStore?.companies || []);
  const [companyDetails, setCompanyDetails] = useState(testStore?.companyDetails || {});
  const [companyTerms, setCompanyTerms] = useState(testStore?.companyTerms || {});
  const [loading, setLoading] = useState(() => !testStore && !readCached(`parcelsData_${farmId}`));
  const [permissions, setPermissions] = useState(null);

  const [year, setYear] = useState(new Date().getFullYear());
  const [segment, setSegment] = useState('waiting');
  const [search, setSearch] = useState('');
  const [company, setCompany] = useState(() => {
    try { return localStorage.getItem(SECTION_KEY); } catch { return null; }
  });
  const [sortBy, setSortBy] = useState('nume');

  const [session, setSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailCnp, setDetailCnp] = useState(null);
  const [panel, setPanel] = useState(null); // 'menu' | 'rates' | 'stats' | 'table'
  const [openDocument, setOpenDocument] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (message, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), type === 'error' ? 6000 : 3500);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Live data: a change on any device shows up here straight away.
  useEffect(() => {
    if (testStore || !farmId) { setLoading(false); return undefined; }
    const stopData = onSnapshot(doc(db, 'farmData', farmId), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const nextParcels = data.parcelsData?.data || data.parcelsData || [];
        setParcels(Array.isArray(nextParcels) ? nextParcels : []);
        setRecords(data.arendaData?.data || []);
        setCompanies((data.companySettings?.data?.companies || []).map(nameOf).filter(Boolean));
        setCompanyDetails(data.companySettings?.data?.companyDetails || {});
      }
      setLoading(false);
    }, (error) => {
      console.error('Arendă: live data failed', error);
      setLoading(false);
    });
    const stopFarm = onSnapshot(doc(db, 'farms', farmId), (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      if (data.grauRates) {
        setRates(correctRates(data.grauRates));
        try { localStorage.setItem(`farms_${farmId}_settings_grauRates`, JSON.stringify(data.grauRates)); } catch { /* cache only */ }
      }
      if (data.customDistributionTypes) setTypes(data.customDistributionTypes);
      setCompanyTerms(data.companyTerms || {});
    }, (error) => console.error('Arendă: live settings failed', error));
    return () => { stopData(); stopFarm(); };
  }, [farmId, testStore]);

  // Employees only see the companies they were given.
  useEffect(() => {
    if (testStore) return;
    const user = firebaseAuthService.getCurrentSession()?.user;
    if (user?.uid && user.role !== 'manager') {
      accessControlService.getUserPermissions(user.uid).then(setPermissions).catch((error) => console.error('Arendă: permissions failed', error));
    }
  }, [testStore]);

  const allowedCompanies = useMemo(() => {
    const allowed = permissions?.companies;
    return allowed && !allowed.includes('all') ? allowed : null;
  }, [permissions]);

  const people = useMemo(() => {
    const all = buildPeople(parcels);
    return allowedCompanies
      ? all.filter((person) => person.parcels.some((parcel) => allowedCompanies.includes(parcel.firma)))
      : all;
  }, [parcels, allowedCompanies]);

  const yearRecords = useMemo(() => {
    const ofYear = records.filter((record) => Number(record.year) === year);
    if (!allowedCompanies) return ofYear;
    const allowed = new Set(people.map((person) => person.cnp));
    return ofYear.filter((record) => allowed.has(record.cnp));
  }, [records, year, allowedCompanies, people]);

  const recordOf = useMemo(() => {
    const byCnp = new Map();
    yearRecords.forEach((record) => { if (!byCnp.has(record.cnp)) byCnp.set(record.cnp, record); });
    return byCnp;
  }, [yearRecords]);

  // Someone paid this year whose parcels are gone must still be findable.
  const orphans = useMemo(() => {
    const known = new Set(people.map((person) => person.cnp));
    return [...recordOf.values()].filter((record) => !known.has(record.cnp)).map((record) => ({
      nume: record.nume || 'N/A', cnp: record.cnp, totalSuprafata: Number(record.totalSuprafata || record.hectares) || 0,
      parcels: [], tGroups: [], contractExpiration: null, orphan: true,
    }));
  }, [people, recordOf]);

  const allTypes = useMemo(() => allTypesOf(companyTerms, types), [companyTerms, types]);
  const termsOf = (name) => termsFor(name, companyTerms, rates, types);
  const ratesOfPerson = (person) => termsOf(person?.firma).rates;

  // One section per company, so nobody's arenda is done with another company's prices.
  const sectionOf = (person) => {
    if (person.orphan) return '';
    if (allowedCompanies && !allowedCompanies.includes(person.firma)) return person.parcels.find((parcel) => allowedCompanies.includes(parcel.firma))?.firma || '';
    return person.firma || '';
  };
  const allPeople = useMemo(() => [...people, ...orphans], [people, orphans]);
  const sections = useMemo(() => {
    const allowed = allowedCompanies ? companies.filter((name) => allowedCompanies.includes(name)) : companies;
    const extra = [...new Set(allPeople.map(sectionOf))].filter((name) => !allowed.includes(name));
    return [...allowed, ...extra];
  }, [companies, allowedCompanies, allPeople]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opens on the last company used, otherwise on the first one that has people.
  const openSection = sections.includes(company) ? company
    : sections.find((name) => allPeople.some((person) => sectionOf(person) === name)) ?? sections[0] ?? '';
  const chooseSection = (name) => {
    setCompany(name);
    setSearch('');
    try { localStorage.setItem(SECTION_KEY, name); } catch { /* only a convenience */ }
  };
  const sectionName = (name) => name || 'Fără firmă';
  const openTerms = termsOf(openSection);

  const everyone = useMemo(() => allPeople.filter((person) => sectionOf(person) === openSection), [allPeople, openSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const cnps = new Set(everyone.map((person) => person.cnp));
    return yearStatistics(everyone, yearRecords.filter((record) => cnps.has(record.cnp)), ratesOfPerson, allTypes);
  }, [everyone, yearRecords, companyTerms, rates, types, allTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const inSegment = everyone.filter((person) => segment === 'all' || (segment === 'received') === recordOf.has(person.cnp));
    return sortPeople(inSegment.filter((person) => matchesSearch(person, search)), sortBy);
  }, [everyone, segment, recordOf, search, sortBy]);

  // A search with nothing here can still point to the person's company.
  const foundElsewhere = useMemo(() => {
    if (!search.trim() || shown.length > 0) return [];
    return [...new Set(allPeople.filter((person) => matchesSearch(person, search)).map(sectionOf))].filter((name) => name !== openSection);
  }, [search, shown, allPeople, openSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const detailPerson = useMemo(() => (
    detailCnp ? people.find((person) => person.cnp === detailCnp) || orphans.find((person) => person.cnp === detailCnp) : null
  ), [detailCnp, people, orphans]);

  // Reads the newest list right before writing, so two devices cannot overwrite each other.
  const saveRecords = async (change) => {
    if (testStore) {
      const next = change(testStore.arenda);
      if (next) { testStore.arenda = next; testStore.saves += 1; setRecords(next); }
      return Boolean(next);
    }
    const ref = doc(db, 'farmData', farmId);
    return runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const next = change(snapshot.data()?.arendaData?.data || []);
      if (!next) return false;
      const now = new Date().toISOString();
      transaction.set(ref, { arendaData: { data: next, lastModified: now, dataType: 'arendaData' }, lastModified: now }, { merge: true });
      setRecords(next);
      return true;
    });
  };

  const saveSettings = async (patch) => {
    if (testStore) { Object.assign(testStore, patch.grauRates ? { rates: patch.grauRates } : {}, patch.customDistributionTypes ? { types: patch.customDistributionTypes } : {}, patch.companyTerms ? { companyTerms: patch.companyTerms } : {}); return; }
    await setDoc(doc(db, 'farms', farmId), { ...patch, lastUpdated: new Date() }, { merge: true });
  };

  const openChitanta = (record) => {
    const person = people.find((item) => item.cnp === record.cnp);
    const firm = person?.firma || '';
    setOpenDocument({ title: `Chitanță ${record.nume}`, html: chitantaHtml(record, allTypes, { company: firm, details: companyDetails[firm] || {}, person }) });
  };

  const finishDistribution = async () => {
    if (saving || !canFinish(session, ratesOfPerson(session.person))) return;
    setSaving(true);
    const isEdit = Boolean(session.record);
    const record = buildRecord(session, year);
    try {
      const saved = await saveRecords((current) => applyRecord(current, record, isEdit));
      if (!saved) {
        showToast(`${record.nume} are deja distribuția pe ${year}, salvată de pe alt dispozitiv. Nu am salvat încă una.`, 'error');
        setSession(null);
        return;
      }
      setSession(null);
      setDetailCnp(null);
      openChitanta(record);
      showToast(isEdit ? 'Distribuția a fost modificată.' : 'Distribuția a fost salvată.');
    } catch (error) {
      console.error('Arendă: save failed', error);
      showToast('Nu s-a salvat. Verifică internetul și încearcă din nou.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const closeSession = () => {
    if (!session.changed) { setSession(null); return; }
    setConfirm({
      title: 'Renunți la distribuție?',
      text: 'Ce ai completat nu a fost salvat.',
      action: 'Renunță',
      onYes: () => { setSession(null); setConfirm(null); },
    });
  };

  const askDelete = (person) => setConfirm({
    title: 'Ștergi distribuția?',
    text: `Se șterge distribuția din ${year} pentru ${person.nume}. Persoana rămâne în sistem, iar distribuția se poate restaura din Coșul de gunoi, în Setări.`,
    action: 'Șterge distribuția',
    onYes: async () => {
      setConfirm(null);
      const { cnp, nume } = person;
      try {
        const removed = recordOf.get(cnp);
        if (testStore) {
          testStore.deleted.push(removed);
        } else if (removed) {
          const current = await firebaseAuthService.getCurrentUser().catch(() => null);
          deletedItemsService.saveToRecycleBin(farmId, removed, 'arenda', `Arendă ${nume} (CNP: ${cnp}) — ${year}`, current?.user?.email || current?.user?.name || 'utilizator');
        }
        await saveRecords((list) => list.filter((record) => !(record.cnp === cnp && Number(record.year) === year)));
        setDetailCnp(null);
        showToast(`Distribuția pentru ${nume} a fost ștearsă. O poți restaura din Coșul de gunoi.`);
      } catch (error) {
        console.error('Arendă: delete failed', error);
        showToast('Nu s-a șters. Verifică internetul și încearcă din nou.', 'error');
      }
    },
  });

  // Saves one company's own prices and products; other companies are not touched.
  const saveTerms = async (nextRates, nextTypes) => {
    if (!openSection) {
      await saveSettings({ grauRates: { ...nextRates, money: 1 }, customDistributionTypes: nextTypes });
      setRates(correctRates(nextRates));
      setTypes(nextTypes);
      return;
    }
    const next = { ...companyTerms, [openSection]: { rates: { ...nextRates, money: 1 }, types: nextTypes } };
    await saveSettings({ companyTerms: next });
    setCompanyTerms(next);
  };

  const saveRates = async (next) => {
    try {
      await saveTerms(next, openTerms.types);
      setPanel(null);
      showToast(`Tarifele pentru ${sectionName(openSection)} au fost salvate.`);
    } catch (error) {
      console.error('Arendă: rates failed', error);
      showToast('Tarifele nu s-au salvat. Verifică internetul.', 'error');
    }
  };

  const addType = async (name, unit, price) => {
    const id = name.toLowerCase().replace(/\s+/g, '');
    if (openTerms.types.some((type) => type.id === id)) {
      showToast(`Produsul ${name} există deja la această firmă.`, 'error');
      return null;
    }
    try {
      await saveTerms({ ...openTerms.rates, [id]: price }, [...openTerms.types, { id, name, unit: unit === 'l' ? 'litri' : unit, unitType: unit }]);
      showToast(`${name} a fost adăugat la ${sectionName(openSection)}.`);
      return id;
    } catch (error) {
      console.error('Arendă: add product failed', error);
      showToast('Produsul nu s-a salvat. Verifică internetul.', 'error');
      return null;
    }
  };

  const askRemoveType = (type) => setConfirm({
    title: `Ștergi ${type.name}?`,
    text: `${type.name} nu va mai apărea la distribuțiile noi ale firmei ${sectionName(openSection)}. Distribuțiile deja făcute rămân neschimbate.`,
    action: 'Șterge produsul',
    onYes: async () => {
      setConfirm(null);
      const nextRates = { ...openTerms.rates };
      delete nextRates[type.id];
      try {
        await saveTerms(nextRates, openTerms.types.filter((item) => item.id !== type.id));
        showToast(`${type.name} a fost șters.`);
      } catch (error) {
        console.error('Arendă: remove product failed', error);
        showToast('Nu s-a șters. Verifică internetul.', 'error');
      }
    },
  });

  const tableRecords = useMemo(() => {
    const cnps = new Set(everyone.map((person) => person.cnp));
    return yearRecords.filter((record) => cnps.has(record.cnp)).sort((a, b) => (a.nume || '').localeCompare(b.nume || '', 'ro'));
  }, [yearRecords, everyone]);

  const openTable = () => {
    setPanel(null);
    setOpenDocument({
      title: `Tabel arendă ${year}`,
      html: tableHtml({ records: tableRecords, year, company: sectionName(openSection), people, types: openTerms.types }),
    });
  };

  const openContract = (person) => setOpenDocument({ title: `Contract ${person.nume}`, html: contractHtml(person) });

  const startFor = (person) => {
    setSession(startSession(person, recordOf.get(person.cnp), ratesOfPerson(person)));
  };

  const menuItems = [
    { id: 'rates', label: 'Tarife', icon: ICON.tag },
    { id: 'history', label: 'Istoric', icon: ICON.clock },
    { id: 'stats', label: 'Statistici', icon: ICON.chart },
    { id: 'table', label: 'Tabel arendă', icon: ICON.table },
  ];

  const segments = [
    { id: 'waiting', label: 'Nu au primit', count: stats.waiting },
    { id: 'received', label: 'Au primit', count: stats.received },
    { id: 'all', label: 'Toți', count: stats.total },
  ];

  return (
    <div className="pm ar">
      <header className="pm-head">
        <div className="ar-title">
          <h1>Arendă</h1>
          <select aria-label="Anul" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearsToShow(records).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="pm-head-actions">
          {menuItems.map((item) => (
            <button key={item.id} type="button" className="pm-btn-plain pm-only-wide" onClick={() => setPanel(item.id)}>
              <Icon d={item.icon} size={18} />{item.label}
            </button>
          ))}
          <button type="button" className="pm-icon-btn ar-only-phone" aria-label="Mai multe" onClick={() => setPanel('menu')}><Icon d={ICON.more} /></button>
        </div>
      </header>

      {sections.length > 1 && (
        <div className="pm-segments ar-companies" role="tablist" aria-label="Firma">
          {sections.map((name) => (
            <button key={name || 'none'} type="button" role="tab" aria-selected={openSection === name} className={openSection === name ? 'is-on' : ''} onClick={() => chooseSection(name)}>
              {sectionName(name)}
            </button>
          ))}
        </div>
      )}

      <section className="ar-progress">
        {sections.length > 0 && <p className="ar-progress-company">{sectionName(openSection)}</p>}
        <p><strong>{stats.received}</strong> din {stats.total} arendași au primit</p>
        <div className="ar-bar"><i style={{ width: `${stats.percent}%` }} /></div>
        <p className="ar-progress-sub">{lei(stats.distributed)} din {lei(stats.possible)}</p>
      </section>

      <div className="pm-segments ar-segments" role="group" aria-label="Filtru">
        {segments.map((item) => (
          <button key={item.id} type="button" className={segment === item.id ? 'is-on' : ''} aria-pressed={segment === item.id} onClick={() => setSegment(item.id)}>
            {item.label} <span>{item.count}</span>
          </button>
        ))}
      </div>

      <label className="pm-search">
        <Icon d={ICON.search} />
        <input type="search" placeholder="Caută nume, CNP sau suprafață" aria-label="Caută" value={search} onChange={(e) => setSearch(e.target.value)} />
      </label>

      <div className="pm-chips">
        <select className="pm-chip" aria-label="Ordine" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="nume">Alfabetic</option>
          <option value="suprafata_desc">Suprafață mare</option>
          <option value="suprafata_asc">Suprafață mică</option>
          <option value="contract_expiry">Contract expiră</option>
        </select>
      </div>

      <div className="pm-list">
        {loading && <p className="pm-empty">Se încarcă datele…</p>}
        {!loading && shown.length === 0 && (
          <p className="pm-empty">
            {search ? 'Nu am găsit pe nimeni.' : segment === 'waiting' && stats.total > 0 ? `Toți arendașii au primit în ${year}.` : segment === 'received' ? `Nimeni nu a primit încă în ${year}.` : 'Nu există arendași. Adaugă parcele în Manager Parcele.'}
          </p>
        )}
        {foundElsewhere.map((name) => (
          <button key={name || 'none'} type="button" className="pm-btn-soft" onClick={() => { const term = search; chooseSection(name); setSearch(term); setSegment('all'); }}>
            Găsit la {sectionName(name)}. Deschide
          </button>
        ))}
        {shown.map((person) => {
          const record = recordOf.get(person.cnp);
          const expired = isExpired(person);
          return (
            <div key={person.cnp} className="ar-row">
              <button type="button" className="ar-row-main" onClick={() => setDetailCnp(person.cnp)}>
                <strong>{person.nume}</strong>
                {record
                  ? <span>{lei(recordTotal(record))} · {formatDate(record.takenTimestamp || record.timestamp)}</span>
                  : <span>{formatHa(person.totalSuprafata)} ha · {lei(budgetFor(person.totalSuprafata, ratesOfPerson(person)))}</span>}
                {record && <span className="ar-row-products">{productsLine(record, allTypes)}</span>}
                {expired && <span className="pm-warn">Contract expirat</span>}
                {!expired && isExpiringSoon(person) && <span className="pm-warn">Contract expiră {person.contractExpiration.toLocaleDateString('ro-RO')}</span>}
              </button>
              {record && <span className="ar-got">Primit</span>}
              {!record && !expired && (
                <button type="button" className="pm-btn-accent ar-row-go" aria-label={`Distribuie pentru ${person.nume}`} onClick={() => startFor(person)}>Distribuie</button>
              )}
            </div>
          );
        })}
      </div>

      {panel === 'menu' && (
        <div className="shell-sheet-backdrop" onClick={(e) => e.target === e.currentTarget && setPanel(null)}>
          <div className="shell-sheet" role="dialog" aria-modal="true" aria-label={`Arendă ${year}`}>
            <div className="shell-sheet-head">
              <div><strong>Arendă {year}</strong></div>
              <button type="button" className="shell-sheet-close" onClick={() => setPanel(null)}>Închide</button>
            </div>
            <div className="shell-sheet-list">
              {menuItems.map((item) => (
                <button key={item.id} type="button" className="shell-sheet-row" onClick={() => setPanel(item.id)}>
                  <Icon d={item.icon} />{item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {detailPerson && (
        <PersonPanel person={detailPerson} record={recordOf.get(detailPerson.cnp)}
          history={records.filter((item) => item.cnp === detailPerson.cnp).sort((a, b) => Number(b.year) - Number(a.year) || newestFirst(a, b))} rates={ratesOfPerson(detailPerson)} types={allTypes} year={year}
          onClose={() => setDetailCnp(null)} onDistribute={() => startFor(detailPerson)}
          onReceipt={() => openChitanta(recordOf.get(detailPerson.cnp))} onPrintRecord={openChitanta} onContract={() => openContract(detailPerson)}
          onDelete={() => askDelete(detailPerson)} />
      )}

      {session && (
        <DistributionPanel session={session} setSession={setSession} rates={ratesOfPerson(session.person)} types={termsOf(session.person.firma).types} saving={saving}
          onSave={finishDistribution} onClose={closeSession} />
      )}

      {panel === 'rates' && (
        <RatesPanel key={`${openSection}|${openTerms.types.length}`} company={openSection} own={openTerms.own} rates={openTerms.rates} types={openTerms.types} onSave={saveRates} onAddType={addType} onRemoveType={askRemoveType} onClose={() => setPanel(null)} />
      )}
      {panel === 'history' && (
        <HistoryPanel records={[...tableRecords].sort(newestFirst)} company={openSection} types={allTypes} year={year}
          onOpen={(cnp) => { setPanel(null); setDetailCnp(cnp); }} onClose={() => setPanel(null)} />
      )}
      {panel === 'stats' && <StatsPanel stats={stats} year={year} onClose={() => setPanel(null)} />}
      {panel === 'table' && (
        <TablePanel year={year} company={openSection} total={tableRecords.length} onOpen={openTable} onClose={() => setPanel(null)} />
      )}

      {openDocument && (
        <DocumentPanel title={openDocument.title} html={openDocument.html} onClose={() => setOpenDocument(null)} onError={(message) => showToast(message, 'error')} />
      )}

      {confirm && <ConfirmPanel {...confirm} onClose={() => setConfirm(null)} />}

      {toast && <div className={`pm-toast is-${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
};

export default ArendaManager;
